import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { nl } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Calendar } from "./ui/calendar";
import {
  CalendarDays,
  Clock,
  User,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  Loader2,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS_HOURLY = [
  "09:00", "10:00", "11:00", "12:00", "13:00",
  "14:00", "15:00", "16:00", "17:00", "18:00",
  "19:00", "20:00",
];

const TIME_SLOTS_HALF = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
  "15:00", "15:30", "16:00", "16:30", "17:00", "17:30",
  "18:00", "18:30", "19:00", "19:30", "20:00", "20:30",
];

const SERVICE_TYPES = [
  "Computer reparatie",
  "Laptop reparatie",
  "Netwerk / WiFi",
  "Virus verwijdering",
  "Data herstel",
  "Hulp op afstand",
  "Advies / overig",
];

const LOCATIONS = [
  { id: "aan-huis", label: "Aan huis (regio IJmond)", duration: 60 },
  { id: "werkplaats", label: "Bij werkplaats inleveren", duration: 30 },
  { id: "hulp-op-afstand", label: "Hulp op afstand (Splashtop)", duration: 30 },
];

// Common typo TLDs we want to flag (likely typos)
const TYPO_TLDS = [".ocm", ".con", ".cmo", ".cim", ".vom", ".coom", ".nll", ".ml.", ".n", ".c"];
const VALID_TLD_REGEX = /\.(com|nl|be|de|org|net|info|eu|io|app|dev|me|co|tech|email|xyz|fr|uk|us)$/i;

function validatePhone(phone) {
  const digits = (phone || "").replace(/\D/g, "");
  if (digits.length < 10) return "Voer minimaal 10 cijfers in.";
  if (digits.length > 13) return "Te lang, controleer het nummer.";
  // detect repeating digits (e.g. 0250250250 = 0250 repeated)
  if (/^(\d)\1{8,}$/.test(digits)) return "Vul een geldig telefoonnummer in.";
  // check obvious fakes: same 4-digit sequence repeated
  const seq = digits.substring(0, 4);
  if (seq.length === 4 && digits.split(seq).length - 1 >= 3) {
    return "Vul een geldig telefoonnummer in.";
  }
  // Dutch numbers should start with 06, 0, or +31
  if (!/^(\+?31|0)/.test(digits)) return "Voer een geldig Nederlands nummer in.";
  return "";
}

function validateEmail(email) {
  const e = (email || "").trim().toLowerCase();
  if (!e) return "E-mail is verplicht.";
  // basic structure
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e)) return "Geen geldig e-mailadres.";
  // typo TLDs
  for (const t of TYPO_TLDS) {
    if (e.endsWith(t)) return `Typefout? Bedoelde u .com / .nl?`;
  }
  // valid TLD list
  if (!VALID_TLD_REGEX.test(e)) return "Controleer de extensie van het e-mailadres.";
  return "";
}

const toISODate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const AppointmentDialog = ({ open, onOpenChange, triggerLocation = "hero" }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [busySlots, setBusySlots] = useState([]);
  const [allSlots, setAllSlots] = useState([]);
  const [serviceDuration, setServiceDuration] = useState(60);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    location: "aan-huis",
    description: "",
  });

  const [errors, setErrors] = useState({ phone: "", email: "" });

  // Reset when opening
  useEffect(() => {
    if (open) {
      setStep(1);
      setSuccess(false);
      setDate(null);
      setTime("");
      setErrors({ phone: "", email: "" });
      setForm({
        name: "",
        email: "",
        phone: "",
        service_type: "",
        location: "aan-huis",
        description: "",
      });
    }
  }, [open]);

  // Fetch availability when date or location changes
  useEffect(() => {
    const loadAvailability = async () => {
      if (!date) return;
      setLoadingSlots(true);
      try {
        const res = await axios.get(`${API_BASE}/appointments/availability`, {
          params: { date: toISODate(date), location: form.location },
        });
        setBusySlots(res.data?.busy_slots || []);
        setAllSlots(res.data?.all_slots || []);
        setServiceDuration(res.data?.duration_minutes || 60);
      } catch (e) {
        setBusySlots([]);
        setAllSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };
    loadAvailability();
    // Reset selected time when location changes
    setTime("");
  }, [date, form.location]);

  const isSlotBusy = useMemo(() => {
    return (slot) => busySlots.includes(slot);
  }, [busySlots]);

  const canProceedStep1 = form.location && date && time;

  const handlePhoneBlur = () => {
    setErrors((er) => ({ ...er, phone: validatePhone(form.phone) }));
  };
  const handleEmailBlur = () => {
    setErrors((er) => ({ ...er, email: validateEmail(form.email) }));
  };

  const phoneErr = errors.phone;
  const emailErr = errors.email;
  const canSubmit =
    form.name &&
    form.email && !validateEmail(form.email) &&
    form.phone && !validatePhone(form.phone) &&
    form.service_type &&
    form.description;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    // Final validation
    const phoneError = validatePhone(form.phone);
    const emailError = validateEmail(form.email);
    if (phoneError || emailError) {
      setErrors({ phone: phoneError, email: emailError });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        location: LOCATIONS.find((l) => l.id === form.location)?.label || form.location,
        date: toISODate(date),
        time,
        duration_minutes: serviceDuration,
      };
      const res = await axios.post(`${API_BASE}/appointments`, payload);
      setSuccess(true);
      toast({
        title: "Afspraak aangevraagd!",
        description: res.data?.message || "U ontvangt binnen 24 uur bevestiging.",
      });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const isConflict = e.response?.status === 409;
      toast({
        title: isConflict ? "Tijdslot niet meer beschikbaar" : "Er ging iets mis",
        description:
          detail ||
          "Probeer het opnieuw of bel ons direct op 06-12562049.",
        variant: "destructive",
      });
      if (isConflict) {
        // Go back to step 1 to pick a new slot + refresh availability
        setStep(1);
        setTime("");
        if (date) {
          try {
            const res = await axios.get(`${API_BASE}/appointments/availability`, {
              params: { date: toISODate(date) },
            });
            setBusySlots(res.data?.busy_slots || []);
          } catch {}
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 max-h-[90vh] overflow-y-auto">
        {success ? (
          <div className="p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-5">
              <CheckCircle2 size={32} className="text-emerald-600" />
            </div>
            <DialogTitle className="text-2xl font-bold text-neutral-900 mb-2">
              Afspraak aangevraagd!
            </DialogTitle>
            <DialogDescription className="text-neutral-600 mb-6">
              Bedankt {form.name.split(" ")[0]}! We hebben je aanvraag ontvangen voor{" "}
              <strong className="text-neutral-900">
                {date && toISODate(date)} om {time}
              </strong>
              . Wij controleren je aanvraag zo snel mogelijk en sturen je spoedig een
              bevestiging per e-mail naar{" "}
              <strong className="text-neutral-900">{form.email}</strong>.
            </DialogDescription>
            <div className="bg-neutral-50 border border-neutral-200 p-4 text-sm text-neutral-700 text-left rounded">
              <p className="font-semibold text-neutral-900 mb-2">Wat nu?</p>
              <ul className="space-y-1.5">
                <li className="flex gap-2"><span className="text-neutral-400">1.</span> Wij beoordelen je aanvraag persoonlijk</li>
                <li className="flex gap-2"><span className="text-neutral-400">2.</span> Je ontvangt spoedig een bevestiging per e-mail</li>
                <li className="flex gap-2"><span className="text-neutral-400">3.</span> Vragen of aanpassen? Reageer gerust op de bevestigingsmail</li>
                <li className="flex gap-2"><span className="text-neutral-400">4.</span> Spoed? Bel direct op <a href="tel:+31612562049" className="underline font-semibold">06-12562049</a></li>
              </ul>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="mt-6 bg-neutral-900 hover:bg-black text-white px-6 py-3 text-sm font-medium tracking-wide transition-colors"
            >
              Sluiten
            </button>
          </div>
        ) : (
          <>
            <DialogHeader className="p-6 md:p-8 pb-4 border-b border-neutral-200">
              <DialogTitle className="text-2xl font-bold text-neutral-900">
                Maak een afspraak
              </DialogTitle>
              <DialogDescription className="text-neutral-600">
                Stap {step} van 2 — {step === 1 ? "Kies datum & tijd" : "Uw gegevens"}
              </DialogDescription>
              <div className="flex gap-2 mt-3">
                <div className={`h-1 flex-1 ${step >= 1 ? "bg-neutral-900" : "bg-neutral-200"}`} />
                <div className={`h-1 flex-1 ${step >= 2 ? "bg-neutral-900" : "bg-neutral-200"}`} />
              </div>
            </DialogHeader>

            <div className="p-6 md:p-8">
              {step === 1 && (
                <div className="space-y-6">
                  {/* Location FIRST — determines slot grid */}
                  <div>
                    <label className="text-xs uppercase tracking-wider text-neutral-600 font-medium mb-3 flex items-center gap-2">
                      <MapPin size={14} /> Type afspraak
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {LOCATIONS.map((loc) => (
                        <button
                          key={loc.id}
                          type="button"
                          onClick={() => setForm({ ...form, location: loc.id })}
                          className={`text-left p-3 text-sm border transition-colors ${
                            form.location === loc.id
                              ? "bg-neutral-900 text-white border-neutral-900"
                              : "bg-white border-neutral-300 hover:border-neutral-900"
                          }`}
                        >
                          <div className="font-medium">{loc.label.split(" (")[0]}</div>
                          <div className={`text-xs mt-0.5 ${form.location === loc.id ? "text-neutral-300" : "text-neutral-500"}`}>
                            {loc.duration} min
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-wider text-neutral-600 font-medium mb-3 flex items-center gap-2">
                      <CalendarDays size={14} /> Kies een datum
                    </label>
                    <div className="flex justify-center border border-neutral-200 rounded bg-neutral-50 p-2">
                      <Calendar
                        mode="single"
                        locale={nl}
                        weekStartsOn={1}
                        selected={date}
                        onSelect={(d) => { setDate(d); setTime(""); }}
                        disabled={(d) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const max = new Date();
                          max.setDate(max.getDate() + 60);
                          // Disable weekends (op afspraak — bel)
                          const dow = d.getDay();
                          return d < today || d > max || dow === 0 || dow === 6;
                        }}
                        className="rounded"
                      />
                    </div>
                    <p className="text-xs text-neutral-500 mt-2">
                      Zaterdag & zondag alleen op afspraak — bel <a href="tel:+31612562049" className="underline">06-12562049</a>.
                    </p>
                  </div>

                  {date && (
                    <div>
                      <label className="text-xs uppercase tracking-wider text-neutral-600 font-medium mb-3 flex items-center gap-2">
                        <Clock size={14} /> Kies een tijd ({serviceDuration} min) {loadingSlots && <Loader2 size={12} className="animate-spin" />}
                      </label>
                      <div className={`grid gap-2 ${serviceDuration === 30 ? "grid-cols-4 md:grid-cols-6" : "grid-cols-4"}`}>
                        {(allSlots.length > 0 ? allSlots : (serviceDuration === 30 ? TIME_SLOTS_HALF : TIME_SLOTS_HOURLY)).map((slot) => {
                          const busy = isSlotBusy(slot);
                          const selected = time === slot;
                          return (
                            <button
                              key={slot}
                              type="button"
                              disabled={busy}
                              onClick={() => setTime(slot)}
                              className={`py-2.5 text-sm font-medium border transition-colors ${
                                selected
                                  ? "bg-neutral-900 text-white border-neutral-900"
                                  : busy
                                  ? "bg-neutral-100 text-neutral-400 border-neutral-200 cursor-not-allowed line-through"
                                  : "bg-white text-neutral-900 border-neutral-300 hover:border-neutral-900"
                              }`}
                            >
                              {slot}
                            </button>
                          );
                        })}
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-xs text-neutral-500 flex-wrap">
                        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-white border border-neutral-300" />Beschikbaar</span>
                        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-neutral-900" />Gekozen</span>
                        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-neutral-100 border border-neutral-200" />Bezet</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <div className="p-3 bg-neutral-50 border border-neutral-200 text-sm flex items-center gap-3">
                    <CalendarDays size={16} className="text-neutral-900" />
                    <span className="text-neutral-700">
                      {date && new Intl.DateTimeFormat("nl-NL", {
                        weekday: "long", day: "numeric", month: "long", year: "numeric",
                      }).format(date)} om <strong className="text-neutral-900">{time}</strong>
                      <span className="text-neutral-500 ml-1">
                        ({LOCATIONS.find(l => l.id === form.location)?.label.split(" (")[0]} — {serviceDuration} min)
                      </span>
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs uppercase tracking-wider text-neutral-600 font-medium mb-1.5 flex items-center gap-2">
                        <User size={12} /> Naam *
                      </label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-neutral-900"
                        placeholder="Uw naam"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-neutral-600 font-medium mb-1.5 flex items-center gap-2">
                        <Phone size={12} /> Telefoon *
                      </label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => {
                          setForm({ ...form, phone: e.target.value });
                          if (errors.phone) setErrors({ ...errors, phone: "" });
                        }}
                        onBlur={handlePhoneBlur}
                        className={`w-full border px-3 py-2.5 text-sm outline-none ${phoneErr ? "border-red-400 focus:border-red-500" : "border-neutral-300 focus:border-neutral-900"}`}
                        placeholder="06-12345678"
                      />
                      {phoneErr && <p className="text-xs text-red-600 mt-1">{phoneErr}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-wider text-neutral-600 font-medium mb-1.5 flex items-center gap-2">
                      <Mail size={12} /> E-mail *
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => {
                        setForm({ ...form, email: e.target.value });
                        if (errors.email) setErrors({ ...errors, email: "" });
                      }}
                      onBlur={handleEmailBlur}
                      className={`w-full border px-3 py-2.5 text-sm outline-none ${emailErr ? "border-red-400 focus:border-red-500" : "border-neutral-300 focus:border-neutral-900"}`}
                      placeholder="uw@email.nl"
                    />
                    {emailErr && <p className="text-xs text-red-600 mt-1">{emailErr}</p>}
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-wider text-neutral-600 font-medium mb-1.5">
                      Type dienst *
                    </label>
                    <select
                      value={form.service_type}
                      onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                      className="w-full border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-neutral-900 bg-white"
                    >
                      <option value="">Kies dienst...</option>
                      {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-wider text-neutral-600 font-medium mb-1.5">
                      Omschrijving probleem *
                    </label>
                    <textarea
                      rows="3"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="w-full border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-neutral-900 resize-none"
                      placeholder="Beschrijf kort uw probleem of vraag..."
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Footer buttons */}
            <div className="p-6 md:p-8 pt-0 flex items-center justify-between gap-3 border-t border-neutral-200 mt-2">
              {step > 1 ? (
                <button
                  onClick={() => setStep(step - 1)}
                  className="inline-flex items-center gap-2 px-5 py-3 border border-neutral-300 hover:border-neutral-900 text-sm font-medium"
                >
                  <ArrowLeft size={14} /> Terug
                </button>
              ) : <div />}
              {step < 2 ? (
                <button
                  onClick={() => setStep(2)}
                  disabled={!canProceedStep1}
                  className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-black disabled:bg-neutral-300 text-white px-6 py-3 text-sm font-medium tracking-wide"
                >
                  Volgende <ArrowRight size={14} />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit || submitting}
                  className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-black disabled:bg-neutral-300 text-white px-6 py-3 text-sm font-medium tracking-wide"
                >
                  {submitting && <Loader2 size={14} className="animate-spin" />}
                  {submitting ? "Verzenden..." : "Afspraak aanvragen"}
                  {!submitting && <ArrowRight size={14} />}
                </button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentDialog;
